/**
 * Terminal 82 Card Builder - Type Definitions
 */

// ============================================
// Core Data Types
// ============================================

export interface OHLC {
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface PriceChange {
  percent: number;
  absolute: number;
}

export interface MarketData {
  mcap: number;
  volume: number;
}

export interface DayData {
  date: string;           // ISO format: "2024-12-06"
  dayId: number;          // Days since ETH genesis
  ohlc: OHLC;
  change: PriceChange;
  market: MarketData;
  intraday: number[];     // 24 hourly prices
  intradayVolumes: number[]; // 24 hourly volumes
  note?: {                // Winning community note
    text: string;
    wallet?: string;
    twitter?: string;
    voteCount: number;
    type: 'human' | 'agent';
    agentName?: string;
  };
}

// ============================================
// Rarity System (Single Source of Truth)
// ============================================

/**
 * RARITY TIERS - The 6 rarity levels based on percentage change
 * Thresholds based on absolute daily price movement
 */
export type RarityTier = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';

/**
 * MYTHIC EVENTS - Special event types for Mythic tier cards
 * Each Mythic card has one of these unique identities
 */
export type MythicEvent =
  | 'genesis'      // Day #1 - ETH launch
  | 'black_swan'   // Extreme crash (≤ -30%)
  | 'supernova'    // Extreme pump (≥ +30%)
  | 'special';     // Manually designated special day

/**
 * Special day configuration for manual mythic designation
 */
export interface SpecialDayConfig {
  name: string;           // Display name (e.g., "The Merge")
  description: string;    // Brief description of the event
  color?: string;         // Optional override color (defaults to mythic)
  isGenesis?: boolean;    // True for the Genesis card (uses genesis theme)
}

/**
 * SPECIAL_DAYS - Manual mythic designation for historic events
 * Key is the date in ISO format (YYYY-MM-DD)
 * These override percentage-based classification
 */
export const SPECIAL_DAYS: Record<string, SpecialDayConfig> = {
  // Genesis - The first Terminal 82 card ever minted
  '2026-01-26': { name: 'Genesis', description: 'First Terminal 82 card minted', isGenesis: true },

  // Example future entries (uncomment when needed):
  // '2022-09-15': { name: 'The Merge', description: 'ETH transitions to Proof of Stake' },
  // '2016-06-17': { name: 'DAO Hack', description: 'The DAO exploit - 3.6M ETH stolen' },
} as const;

/**
 * TIER COLORS - For TypeScript runtime use
 * CSS SOURCE: src/styles/base.css (--tier-X variables)
 * Keep these in sync with CSS!
 */
export const TIER_COLORS: Record<RarityTier, string> = {
  common: '#9D9D9D',
  uncommon: '#1EFF00',
  rare: '#0070FF',
  epic: '#A335EE',
  legendary: '#FF8000',
  mythic: '#FF4500',
} as const;

/**
 * TIER LABELS - Display names for each tier
 */
export const TIER_LABELS: Record<RarityTier, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
  mythic: 'Mythic',
} as const;

/**
 * MYTHIC EVENT COLORS - For TypeScript runtime use
 * CSS SOURCE: src/styles/base.css (--mythic-X variables)
 * Keep these in sync with CSS!
 */
export const MYTHIC_EVENT_COLORS: Record<MythicEvent, string> = {
  genesis: '#FFFFFF',
  black_swan: '#FF0000',
  supernova: '#FFD700',
  special: '#FF4500',
} as const;

/**
 * MYTHIC EVENT LABELS - Display names for mythic events
 * Note: 'special' events use their custom name from SPECIAL_DAYS
 */
export const MYTHIC_EVENT_LABELS: Record<MythicEvent, string> = {
  genesis: 'Genesis',
  black_swan: 'Black Swan',
  supernova: 'Supernova',
  special: 'Special Event',  // Default, overridden by SPECIAL_DAYS[date].name
} as const;

// ============================================
// Theme System
// ============================================

/**
 * THEME IDs - Visual themes for cards
 * Base themes match tier names 1:1
 * Special themes for mythic events override base
 */
export type ThemeId = RarityTier | 'genesis' | 'black_swan' | 'supernova';

/**
 * Maps mythic events to their theme overrides
 */
export const MYTHIC_EVENT_THEMES: Record<MythicEvent, ThemeId> = {
  genesis: 'genesis',
  black_swan: 'black_swan',
  supernova: 'supernova',
  special: 'mythic',  // Special events use base mythic theme
} as const;

// ============================================
// Card Configuration
// ============================================

export type CandleDirection = 'bullish' | 'bearish';

export interface CardConfig {
  theme: ThemeId;
  tier: RarityTier;
  direction: CandleDirection;
  mythicEvent?: MythicEvent;
  traits?: string[];
  volumeId?: string;       // Collection volume (e.g., "V1", "V2")
  edition?: string;        // Edition info (e.g., "1/1", "42/100")
}

// ============================================
// Constants
// ============================================

/**
 * Mythic event thresholds (% change)
 * Based on ETH historical analysis - only ~8 days ever hit these
 */
export const MYTHIC_EVENT_THRESHOLDS = {
  black_swan: -30,  // Extreme crash (2 in history)
  supernova: 30,    // Extreme pump (6 in history)
} as const;

/**
 * Percentage-based rarity thresholds
 * Based on absolute daily price change percentage
 *
 * Common:    <4%
 * Uncommon:  4-7%
 * Rare:      7-12%
 * Epic:      12-20%
 * Legendary: 20-30%
 * Mythic:    >30%
 */
export const PERCENT_THRESHOLDS = {
  uncommon: 4,
  rare: 7,
  epic: 12,
  legendary: 20,
  mythic: 30,
} as const;

export const CANDLE_SIZE = {
  min: 55,
  max: 150,
  scale: 3,
} as const;

// ============================================
// Rarity Configuration
// ============================================

export interface RarityConfig {
  tier: RarityTier;
  theme: ThemeId;
  direction: CandleDirection;
  mythicEvent?: MythicEvent;
}

/**
 * Get rarity tier from absolute percentage change
 * Uses fixed percentage thresholds matching UI display
 */
export function getRarityTier(changePercent: number): RarityTier {
  const absChange = Math.abs(changePercent);
  if (absChange >= PERCENT_THRESHOLDS.mythic) return 'mythic';
  if (absChange >= PERCENT_THRESHOLDS.legendary) return 'legendary';
  if (absChange >= PERCENT_THRESHOLDS.epic) return 'epic';
  if (absChange >= PERCENT_THRESHOLDS.rare) return 'rare';
  if (absChange >= PERCENT_THRESHOLDS.uncommon) return 'uncommon';
  return 'common';
}

/**
 * Get mythic event type from price change or special day designation
 * Priority: 1) Genesis, 2) SPECIAL_DAYS, 3) Black Swan/Supernova thresholds
 * Note: Genesis is designated via SPECIAL_DAYS with isGenesis: true
 */
export function getMythicEvent(changePercent: number, _dayId?: number, date?: string): MythicEvent | undefined {
  // 1. Check for manually designated special days
  if (date && SPECIAL_DAYS[date]) {
    // Genesis gets its own mythic event type (white theme)
    if (SPECIAL_DAYS[date].isGenesis) return 'genesis';
    return 'special';
  }

  // 2. Check percentage thresholds for extreme events
  if (changePercent <= MYTHIC_EVENT_THRESHOLDS.black_swan) return 'black_swan';
  if (changePercent >= MYTHIC_EVENT_THRESHOLDS.supernova) return 'supernova';

  return undefined;
}

/**
 * Get special day configuration for a given date
 * Returns undefined if date is not a special day
 */
export function getSpecialDayConfig(date: string): SpecialDayConfig | undefined {
  return SPECIAL_DAYS[date];
}

/**
 * Get complete rarity configuration from percentage change
 * Single source of truth for tier, theme, direction, and mythic event
 * @param changePercent - The raw % change
 * @param dayId - Optional day ID (for genesis detection)
 * @param date - Optional date string (for SPECIAL_DAYS lookup)
 */
export function getRarityConfig(changePercent: number, dayId?: number, date?: string): RarityConfig {
  const direction: CandleDirection = changePercent >= 0 ? 'bullish' : 'bearish';
  const mythicEvent = getMythicEvent(changePercent, dayId, date);

  // Mythic events override tier and theme
  if (mythicEvent) {
    return {
      tier: 'mythic',
      theme: MYTHIC_EVENT_THEMES[mythicEvent],
      direction: mythicEvent === 'black_swan' ? 'bearish' : direction,
      mythicEvent,
    };
  }

  // Normal tiers - use percentage-based classification
  const tier = getRarityTier(changePercent);
  return {
    tier,
    theme: tier,  // Theme matches tier name 1:1
    direction,
  };
}

/**
 * Get display label for a card
 * Returns tier name or mythic event name
 * @param config - The rarity configuration
 * @param date - Optional date for special day name lookup
 */
export function getDisplayLabel(config: RarityConfig, date?: string): string {
  if (config.mythicEvent) {
    // Special events use their custom name from SPECIAL_DAYS
    if (config.mythicEvent === 'special' && date) {
      const specialDay = getSpecialDayConfig(date);
      if (specialDay) {
        return specialDay.name.toUpperCase();
      }
    }
    return MYTHIC_EVENT_LABELS[config.mythicEvent].toUpperCase();
  }
  return TIER_LABELS[config.tier].toUpperCase();
}

/**
 * Get display color for a card
 * Returns tier color or mythic event color
 * @param config - The rarity configuration
 * @param date - Optional date for special day color lookup
 */
export function getDisplayColor(config: RarityConfig, date?: string): string {
  if (config.mythicEvent) {
    // Special events can have custom colors
    if (config.mythicEvent === 'special' && date) {
      const specialDay = getSpecialDayConfig(date);
      if (specialDay?.color) {
        return specialDay.color;
      }
    }
    return MYTHIC_EVENT_COLORS[config.mythicEvent];
  }
  return TIER_COLORS[config.tier];
}
